'use client';

// =============================================================================
// GOOGLE CUSTOMER REVIEWS — BADGE INTEGRATION COMPONENT
// Merchant ID: 5805303516 (Parts4Drive Parent MCA)
// Displays participation in Google Customer Reviews and verified seller rating
// =============================================================================

import React, { useEffect } from 'react';

declare global {
  interface Window {
    merchantwidget?: {
      start: (config: {
        merchant_id: number;
        position?: string; // "BOTTOM_RIGHT", "BOTTOM_LEFT", "INLINE"
        region?: string;   // e.g. "GH", "NG"
      }) => void;
    };
  }
}

interface GoogleBadgeProps {
  merchantId?: number;
  position?: 'BOTTOM_RIGHT' | 'BOTTOM_LEFT' | 'INLINE';
  region?: string;
  className?: string;
}

export default function GoogleCustomerReviewsBadge({
  merchantId = 5805303516,
  position = 'BOTTOM_RIGHT',
  region = 'GH',
  className = '',
}: GoogleBadgeProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (/googlebot|google-inspectiontool|lighthouse|chrome-lighthouse/i.test(navigator.userAgent)) {
      return;
    }

    const startWidget = () => {
      if (window.merchantwidget && typeof window.merchantwidget.start === 'function') {
        try {
          window.merchantwidget.start({
            merchant_id: merchantId,
            position: position,
            region: (region || 'GH').toUpperCase(),
          });
          console.log('[GCR] Google Customer Reviews badge initialized for MCA:', merchantId);

          // Accessibility Guardian: Ensure Google's injected iframe has an accessible title
          const ensureIframeTitle = () => {
            const iframes = document.querySelectorAll('iframe');
            iframes.forEach((iframe) => {
              if (iframe.id === 'merchantwidgetiframe' || iframe.closest('#google-merchantwidget-iframe-wrapper') || iframe.src.includes('shopping/merchant')) {
                if (!iframe.getAttribute('title')) {
                  iframe.setAttribute('title', 'Google Customer Reviews');
                  iframe.setAttribute('aria-label', 'Google Customer Reviews');
                }
              }
            });
          };

          const observer = new MutationObserver(ensureIframeTitle);
          observer.observe(document.body, { childList: true, subtree: true });
          setTimeout(ensureIframeTitle, 500);
          setTimeout(ensureIframeTitle, 1500);
          setTimeout(ensureIframeTitle, 3000);
        } catch (e: any) {
          console.warn('[GCR] Widget start notice:', e.message);
        }
      }
    };

    let script = document.getElementById('merchantWidgetScript') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'merchantWidgetScript';
      script.src = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js';
      script.defer = true;
      script.addEventListener('load', startWidget);
      document.body.appendChild(script);
    } else {
      if (window.merchantwidget) {
        startWidget();
      } else {
        script.addEventListener('load', startWidget);
      }
    }
  }, [merchantId, position, region]);

  if (position === 'INLINE') {
    return (
      <div className={`google-customer-reviews-inline-badge ${className}`}>
        {/* Google Customer Reviews inline container */}
        <div id="gcr-inline-target" className="min-h-[54px] flex items-center justify-center p-2 rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  // For floating positions (BOTTOM_RIGHT, BOTTOM_LEFT), Google script injects its own floating iframe
  return null;
}
